import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetShipmentDeliveryListQuery } from '../../../api/endpoints/shipmentDelivery';
import { useLanguage } from '../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../utils/filterUtils';
import { formatDateForApi, getYesterdayDate } from '../../../utils/dateUtils';

const getDefaultDateRange = () => ({
    startDate: formatDateForApi(getYesterdayDate()),
    endDate: null
});

const toApiDate = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    return formatDateForApi(value instanceof Date ? value : new Date(value)) || null;
};

const backendColumnMap = {
    kunnr: 'Kunnr',
    eFaturaNo: 'Efatura',
    irsaliyeNo: 'Eirsaliye',
    siparisKalemKodu: 'Matnr',
    musteriSiparisNo: 'Bstkd',
    miktar: 'Fkimg',
    birim: 'Vrkme',
    birimFiyat: 'KlmBirimFyt',
    netTutar: 'Netwr',
    paraBirimi: 'Waerk',
    nakliyeci: 'Traid',
    nakliyeciKonsimento: 'Bolnr',
    cikisDeposu: 'Lgort',
    siparisOnceligi: 'Lprio'
};
const SPECIAL_PARAM_MAP = {
    customerInfo: 'customerName',
    kalemTanimi: 'kalemTanimi',
    siparisiOlusturanBayi: 'siparisiOlusturanBayi',
    siparisNumarasiReferansi: 'siparisNoRef'
};

const DATE_SPECIAL_MAP = {
    irsaliyeTarihi: 'irsaliyeTarihi'
};

const EXACT_FIELDS = new Set(['cikisDeposu', 'siparisOnceligi']);
const MULTI_CODE_FIELDS = new Set(['siparisOnceligi']);

export const INITIAL_FILTER_STATE = Object.keys({
    ...backendColumnMap,
    ...SPECIAL_PARAM_MAP,
    ...DATE_SPECIAL_MAP
}).reduce((acc, f) => ({ ...acc, [f]: null, [`${f}MatchMode`]: null }), {});

const getOperatorValue = (mm) => matchModeToOperator[mm] ?? matchModeToOperator[FilterMatchMode.CONTAINS];

const buildDynamicFilters = (state) => {
    const filters = [];
    Object.entries(backendColumnMap).forEach(([field, column]) => {
        const value = state?.[field];
        if (value === null || value === undefined || value === '') return;

        const matchMode = EXACT_FIELDS.has(field) ? FilterMatchMode.EQUALS : state?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS;

        const raw = Array.isArray(value) ? value : MULTI_CODE_FIELDS.has(field) ? String(value).split(',') : [value];

        raw.map((v) => String(v ?? '').trim())
            .filter(Boolean)
            .forEach((v) => filters.push({ column, operator: getOperatorValue(matchMode), value: v }));
    });
    return filters;
};

const buildSpecialParams = (state) => {
    const params = {};

    Object.entries(SPECIAL_PARAM_MAP).forEach(([field, param]) => {
        const v = state?.[field];
        if (v !== null && v !== undefined && String(v).trim() !== '') params[param] = String(v).trim();
    });

    Object.entries(DATE_SPECIAL_MAP).forEach(([field, param]) => {
        const v = state?.[field];
        if (v) params[param] = toApiDate(v);
    });

    return params;
};

const useShipmentDelivery = () => {
    const { getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10 });
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [dateRange, setDateRangeState] = useState(getDefaultDateRange);
    const [filterUiKey, setFilterUiKey] = useState(0);

    const languageId = useMemo(() => getCurrentLanguageId(), [getCurrentLanguageId]);
    const sortDirection = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : null;

    const baseParams = useMemo(() => {
        const startDate = dateRange.startDate || getDefaultDateRange().startDate;
        const params = {
            languageId,
            startDate,
            endDate: dateRange.endDate,
            sortBy: sortField,
            sortDirection,
            ...buildSpecialParams(activeFilters)
        };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        return params;
    }, [activeFilters, dateRange, languageId, sortField, sortDirection]);

    const queryParams = useMemo(() => ({ ...baseParams, pageNumber: lazyParams.page, pageSize: lazyParams.rows }), [baseParams, lazyParams.page, lazyParams.rows]);

    const { data, isLoading, isFetching, refetch } = useGetShipmentDeliveryListQuery(queryParams, { skip: !queryParams.startDate });

    const resetPage = () => setLazyParams((p) => ({ ...p, page: 1 }));

    return {
        items: data?.rows ?? [],
        totalRecords: data?.totalCount ?? 0,
        isLoading: isLoading || isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        dateRange,
        filterUiKey,
        exportParams: baseParams,
        refetch,
        setDateRange: (range) => {
            setDateRangeState({
                startDate: range?.startDate || getDefaultDateRange().startDate,
                endDate: range?.endDate || null
            });
            setFilterUiKey((k) => k + 1);
            resetPage();
        },
        onPage: (e) => setLazyParams({ page: e.page + 1, rows: e.rows }),
        onSort: (e) => {
            setSortField(e?.sortField || null);
            setSortOrder(e?.sortOrder || null);
            resetPage();
        },
        onFiltersChange: (f) => {
            setActiveFilters(f);
            const irsaliyeApi = toApiDate(f?.irsaliyeTarihi);
            if (irsaliyeApi) {
                setDateRangeState((prev) => {
                    if (!prev.startDate || irsaliyeApi < prev.startDate) {
                        return { ...prev, startDate: irsaliyeApi };
                    }
                    return prev;
                });
            }
            resetPage();
        },
        initFilters: () => {
            setActiveFilters(INITIAL_FILTER_STATE);
            setDateRangeState(getDefaultDateRange());
            setFilterUiKey((k) => k + 1);
            setSortField(null);
            setSortOrder(null);
            setLazyParams({ page: 1, rows: 10 });
        }
    };
};

export default useShipmentDelivery;
